import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { auth, db } from '../lib/firebase'
import { doc, getDoc } from 'firebase/firestore'
import {
  TrendingUp,
  Trophy,
  Target,
  AlertCircle,
  Calendar,
  ChevronRight,
  Zap,
  Star
} from 'lucide-react'

const Dashboard = () => {
  const [userData, setUserData] = useState(null)
  const [todaysGames, setTodaysGames] = useState([])
  const [insights, setInsights] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      // Load user data
      if (auth.currentUser) {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid))
        if (userDoc.exists()) {
          setUserData(userDoc.data())
        }
      }

      // Mock data for now - will be replaced with real API calls
      setTodaysGames([
        {
          id: '1',
          homeTeam: 'Lakers',
          awayTeam: 'Warriors',
          time: '7:30 PM',
          sport: 'NBA',
          prediction: { confidence: 78, pick: 'Lakers -3.5' }
        },
        {
          id: '2',
          homeTeam: 'Celtics',
          awayTeam: 'Nets',
          time: '8:00 PM',
          sport: 'NBA',
          prediction: { confidence: 85, pick: 'Over 224.5' }
        }
      ])

      setInsights([
        {
          type: 'injury',
          message: 'Stephen Curry questionable for tonight',
          impact: 'high'
        },
        {
          type: 'trend',
          message: 'Lakers 8-2 ATS in last 10 home games',
          impact: 'medium'
        },
        {
          type: 'value',
          message: 'Celtics -7.5 showing value at current odds',
          impact: 'high'
        }
      ])
    } catch (error) {
      console.error('Error loading dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-6 text-white">
        <h1 className="text-3xl font-bold mb-2">
          Welcome back, {userData?.name || auth.currentUser?.displayName || 'Player'}!
        </h1>
        <p className="text-primary-100">
          Level {userData?.level || 1} • {userData?.xp || 0} XP • {userData?.streak || 0} day streak
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-dark-card border border-dark-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Win Rate</p>
              <p className="text-2xl font-bold text-white">67.5%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Predictions</p>
              <p className="text-2xl font-bold text-white">245</p>
            </div>
            <Target className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Best Streak</p>
              <p className="text-2xl font-bold text-white">12</p>
            </div>
            <Zap className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-dark-card border border-dark-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Rank</p>
              <p className="text-2xl font-bold text-white">#127</p>
            </div>
            <Trophy className="h-8 w-8 text-primary-500" />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Games */}
        <div className="lg:col-span-2 bg-dark-card border border-dark-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Today's Best Bets
            </h2>
            <Link to="/games" className="text-primary-500 hover:text-primary-400 flex items-center gap-1">
              View All <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="space-y-3">
            {todaysGames.map((game) => (
              <Link
                key={game.id}
                to={`/game/${game.id}`}
                className="block bg-gray-800/50 rounded-lg p-4 hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-primary-500/20 text-primary-400 px-2 py-1 rounded">
                        {game.sport}
                      </span>
                      <span className="text-gray-400 text-sm">{game.time}</span>
                    </div>
                    <p className="text-white font-medium">
                      {game.awayTeam} @ {game.homeTeam}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      AI Pick: {game.prediction.pick}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-500">
                      {game.prediction.confidence}%
                    </div>
                    <p className="text-xs text-gray-400">Confidence</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Insights Panel */}
        <div className="bg-dark-card border border-dark-border rounded-lg p-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-4">
            <AlertCircle className="h-5 w-5" />
            Key Insights
          </h2>

          <div className="space-y-3">
            {insights.map((insight, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  insight.impact === 'high'
                    ? 'bg-red-500/10 border-red-500/20'
                    : 'bg-yellow-500/10 border-yellow-500/20'
                }`}
              >
                <p className={`text-sm ${
                  insight.impact === 'high' ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {insight.message}
                </p>
              </div>
            ))}
          </div>

          <Link
            to="/analytics"
            className="mt-4 w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            View Full Analytics
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Daily Challenge */}
      <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Daily Challenge
            </h3>
            <p className="text-gray-300">
              Make 3 correct NBA predictions today
            </p>
            <div className="mt-3">
              <div className="bg-gray-700 rounded-full h-2 w-64">
                <div className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full" style={{width: '33%'}}></div>
              </div>
              <p className="text-sm text-gray-400 mt-1">1/3 completed</p>
            </div>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-yellow-500">+50 XP</p>
            <p className="text-sm text-gray-400">Reward</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard